-- | This module takes the place of the old `SearchSubForm` module for the
-- | purposes of backwards compatibility.
-- |
-- | It simply wraps an `AutoFillSelect` and a `SubForm` into a single widget
-- | which emulates the behavior of the previous widget. Ideally, use of this
-- | should be replaced with standalone `AutoFillSelect` widgets later when we
-- | have better ways to change existing form layouts.
module Widgets.Form.AutoFillSubForm where

import Common

import API.Expectations (GroupCourse)
import API.SelEmployer (PosConEmployer)
import Concur.React.DOM as D
import Concur.React.Props as P
import Data.Argonaut as J
import Data.Array as Arr
import Data.Maybe (maybe)
import Foreign.Object (Object)
import Foreign.Object as O
import Widgets.BetterSelect (selectedItem)
import Widgets.Form.AutoFillSelect (AutoFillSelect(..), SubSelect, afSelectedId, courseAutoFillSelect, employerAutoFillSelect)
import Widgets.Form.Class (class FormWidget, addParentId, fieldCanView, formWidgetState, getData, getId, fieldPaths, setData, toWidget)
import Widgets.Form.SubForm (SubForm(..))


courseSubForm :: SubForm -> AutoFillSubForm GroupCourse
courseSubForm subform@(SubForm { id }) = initAutoFillSubForm
    { id, subform
    , select: courseAutoFillSelect { id: (id <> "-afsf-select") }
    }

employerSubForm :: SubForm -> AutoFillSubForm PosConEmployer
employerSubForm subform@(SubForm { id }) = initAutoFillSubForm
    { id, subform
    , select: employerAutoFillSelect { id: (id <> "-afsf-select") }
    }

initAutoFillSubForm :: forall a. AutoFillSubFormType a -> AutoFillSubForm a
initAutoFillSubForm afsf =
    AutoFillSubForm $ afsf { subform = updateSF afsf.subform }
  where
    -- Search subforms are always collapsible.
    updateSF (SubForm sf) = SubForm $ sf { collapsible = true }

newtype AutoFillSubForm a = AutoFillSubForm (AutoFillSubFormType a)
type AutoFillSubFormType a =
    { id :: String
    , select :: AutoFillSelect a
    , subform :: SubForm
    }
instance showAFSF :: Show (AutoFillSubForm a) where
      show _ = "AutoFillSubForm"

instance afsfFW :: FormWidget (AutoFillSubForm a) where
    getId (AutoFillSubForm {id}) = id
    isLeaf _ = false
    asField _ = Nothing
    fieldPaths fc (AutoFillSubForm state) =
        fieldPaths fc' state.select <> fieldPaths fc' state.subform
      where
        fc' = addParentId fc state.id
    toWidget fc (AutoFillSubForm state) = map AutoFillSubForm <$> D.div
        [ P.className "search-subform", P.key state.id ]
        [ D.div [ P.className "search-subform-search" ] [ searchWidget ]
        , D.div [ P.className "search-subform-body" ] [ bodyWidget ]
        ]
      where
        -- This ensures locking a form also locks children that are auto-fill
        -- subforms.
        fc' = addParentId fc state.id
        searchWidget
            | fieldCanView fc' (getId state.select) =
                map state { select = _ } <$> toWidget fc' state.select
            | otherwise = formWidgetState state <$ D.div [ P.className "hidden" ] []
        bodyWidget = map state { subform = _ } <$> toWidget fc' state.subform
    getData fc (AutoFillSubForm state@{ select: AutoFillSelect afs }) = obj
      where
        addIDs :: Object Json -> Json
        addIDs json = J.fromObject $ O.union json selJson

        selJson = O.union mainSelFields subSelFields

        mainSelFields :: Object Json
        mainSelFields = maybe O.empty (\id -> O.fromHomogeneous { id: J.fromString id })
            (afSelectedId state.select)

        subSelFields :: Object Json
        subSelFields = O.fromFoldable subSelIds

        subSelIds = Arr.mapMaybe subSelId afs.subsels

        subSelId :: SubSelect a -> Maybe (Tuple String Json)
        subSelId sstate = do
           sel <- selectedItem sstate.select
           Just $ Tuple (sstate.id <> "Id") $ J.fromString $
               sstate.funcs.itemId sel.value

        obj = J.caseJsonObject sfData addIDs sfData
        sfData = getData fc state.subform

    setData fc (AutoFillSubForm state) json = do
        newsf <- setData fc state.subform json
        newsel <- setData fc state.select json
        pure $ AutoFillSubForm $ state
            { subform = newsf
            , select = newsel
            }
